/**
 * Date Formatter Utilities
 * 날짜 포맷팅 유틸리티
 *
 * 프로젝트 전체에서 일관된 날짜 포맷을 사용하기 위한 유틸리티 함수들입니다.
 * 향후 다국어 지원 시 이 파일만 수정하면 전체 프로젝트에 적용됩니다.
 */

/**
 * 기본 로케일 (향후 i18n으로 대체 가능)
 */
const DEFAULT_LOCALE = 'ko-KR';

/**
 * 날짜만 포맷 (년-월-일)
 * @param date Date 객체 또는 날짜 문자열
 * @param locale 로케일 (기본값: 'ko-KR')
 * @returns 포맷된 날짜 문자열 (예: 2024.11.16)
 *
 * @example
 * formatDate('2024-11-16T10:30:00') // '2024. 11. 16.'
 * formatDate(new Date()) // '2024. 11. 16.'
 */
export const formatDate = (date: Date | string, locale: string = DEFAULT_LOCALE): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString(locale);
};

/**
 * 날짜와 시간 포맷 (년-월-일 시:분)
 * @param date Date 객체 또는 날짜 문자열
 * @param locale 로케일 (기본값: 'ko-KR')
 * @returns 포맷된 날짜시간 문자열 (예: 2024. 11. 16. 오후 2:30)
 *
 * @example
 * formatDateTime('2024-11-16T14:30:00') // '2024. 11. 16. 오후 2:30'
 */
export const formatDateTime = (date: Date | string, locale: string = DEFAULT_LOCALE): string => {
  if (!date) return '-';
  return new Date(date).toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 날짜와 시간 포맷 (상세: 년-월-일 시:분:초)
 * @param date Date 객체 또는 날짜 문자열
 * @param locale 로케일 (기본값: 'ko-KR')
 * @returns 포맷된 날짜시간 문자열 (초 포함)
 *
 * @example
 * formatDateTimeFull('2024-11-16T14:30:45') // '2024. 11. 16. 오후 2:30:45'
 */
export const formatDateTimeFull = (
  date: Date | string,
  locale: string = DEFAULT_LOCALE
): string => {
  if (!date) return '-';
  return new Date(date).toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

/**
 * 짧은 날짜 포맷 (월-일)
 * @param date Date 객체 또는 날짜 문자열
 * @param locale 로케일 (기본값: 'ko-KR')
 * @returns 포맷된 짧은 날짜 문자열 (예: 11. 16.)
 *
 * @example
 * formatDateShort('2024-11-16') // '11. 16.'
 */
export const formatDateShort = (date: Date | string, locale: string = DEFAULT_LOCALE): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * 시간만 포맷 (시:분)
 * @param date Date 객체 또는 날짜 문자열
 * @param locale 로케일 (기본값: 'ko-KR')
 * @returns 포맷된 시간 문자열 (예: 오후 2:30)
 *
 * @example
 * formatTime('2024-11-16T14:30:00') // '오후 2:30'
 */
export const formatTime = (date: Date | string, locale: string = DEFAULT_LOCALE): string => {
  if (!date) return '-';
  return new Date(date).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 상대 시간 포맷 (예: 3일 전, 2시간 전)
 * @param date Date 객체 또는 날짜 문자열
 * @returns 상대 시간 문자열
 *
 * @example
 * formatRelativeTime('2024-11-13T10:00:00') // '3일 전' (현재가 2024-11-16일 경우)
 */
export const formatRelativeTime = (date: Date | string): string => {
  if (!date) return '-';

  const now = new Date();
  const targetDate = new Date(date);
  const diffMs = now.getTime() - targetDate.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return '방금 전';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}분 전`;
  } else if (diffHours < 24) {
    return `${diffHours}시간 전`;
  } else if (diffDays < 7) {
    return `${diffDays}일 전`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks}주 전`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months}개월 전`;
  } else {
    const years = Math.floor(diffDays / 365);
    return `${years}년 전`;
  }
};

/**
 * 날짜 범위 포맷
 * @param startDate 시작 날짜
 * @param endDate 종료 날짜
 * @param locale 로케일 (기본값: 'ko-KR')
 * @returns 포맷된 날짜 범위 문자열 (예: 2024. 11. 1. ~ 2024. 11. 16.)
 *
 * @example
 * formatDateRange('2024-11-01', '2024-11-16') // '2024. 11. 1. ~ 2024. 11. 16.'
 */
export const formatDateRange = (
  startDate: Date | string,
  endDate: Date | string,
  locale: string = DEFAULT_LOCALE
): string => {
  if (!startDate || !endDate) return '-';
  return `${formatDate(startDate, locale)} ~ ${formatDate(endDate, locale)}`;
};

/**
 * ISO 8601 형식으로 변환 (API 전송용)
 * @param date Date 객체
 * @returns ISO 8601 형식 문자열 (예: 2024-11-16T14:30:00.000Z)
 *
 * @example
 * toISOString(new Date()) // '2024-11-16T05:30:00.000Z'
 */
export const toISOString = (date: Date): string => {
  return date.toISOString();
};

/**
 * 한국어 날짜 포맷 (년월일)
 * @param date Date 객체 또는 날짜 문자열
 * @returns 포맷된 한국어 날짜 문자열 (예: 2024년 11월 16일)
 *
 * @example
 * formatDateKorean('2024-11-16') // '2024년 11월 16일'
 * formatDateKorean(new Date()) // '2024년 11월 16일'
 */
export const formatDateKorean = (date: Date | string): string => {
  if (!date) return '-';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  return `${year}년 ${month.toString().padStart(2, '0')}월 ${day.toString().padStart(2, '0')}일`;
};

/**
 * 한국어 날짜 포맷 + 요일 (년월일 요일)
 * V206: 간단예약관리 등에서 요일 표시 필요
 * @param date Date 객체 또는 날짜 문자열
 * @returns 포맷된 한국어 날짜 문자열 (예: 2024년 11월 16일 (토))
 *
 * @example
 * formatDateKoreanWithDay('2024-11-16') // '2024년 11월 16일 (토)'
 * formatDateKoreanWithDay(new Date()) // '2024년 11월 16일 (토)'
 */
export const formatDateKoreanWithDay = (date: Date | string): string => {
  if (!date) return '-';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
  return `${year}년 ${month.toString().padStart(2, '0')}월 ${day.toString().padStart(2, '0')}일 (${dayOfWeek})`;
};

/**
 * 한국어 요일 배열
 */
const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 날짜 + 요일 포맷 (년-월-일 (요일))
 * V206: 프로젝트 전체에서 요일 표시 일관성을 위해 추가
 * @param date Date 객체 또는 날짜 문자열
 * @param locale 로케일 (기본값: 'ko-KR')
 * @returns 포맷된 날짜 문자열 (예: 2024. 11. 16. (토))
 *
 * @example
 * formatDateWithDay('2024-11-16') // '2024. 11. 16. (토)'
 */
export const formatDateWithDay = (date: Date | string, locale: string = DEFAULT_LOCALE): string => {
  if (!date) return '-';
  const d = new Date(date);
  const dayOfWeek = DAY_NAMES[d.getDay()];
  return `${d.toLocaleDateString(locale)} (${dayOfWeek})`;
};

/**
 * 날짜시간 + 요일 포맷 (년-월-일 (요일) 시:분)
 * V206: 프로젝트 전체에서 요일 표시 일관성을 위해 추가
 * @param date Date 객체 또는 날짜 문자열
 * @param locale 로케일 (기본값: 'ko-KR')
 * @returns 포맷된 날짜시간 문자열 (예: 2024. 11. 16. (토) 오후 2:30)
 *
 * @example
 * formatDateTimeWithDay('2024-11-16T14:30:00') // '2024. 11. 16. (토) 오후 2:30'
 */
export const formatDateTimeWithDay = (date: Date | string, locale: string = DEFAULT_LOCALE): string => {
  if (!date) return '-';
  const d = new Date(date);
  const dayOfWeek = DAY_NAMES[d.getDay()];
  const dateStr = d.toLocaleDateString(locale);
  const timeStr = d.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dateStr} (${dayOfWeek}) ${timeStr}`;
};

/**
 * 짧은 날짜 + 요일 포맷 (월-일 (요일))
 * V206: 프로젝트 전체에서 요일 표시 일관성을 위해 추가
 * @param date Date 객체 또는 날짜 문자열
 * @returns 포맷된 짧은 날짜 문자열 (예: 11/16 (토))
 *
 * @example
 * formatDateShortWithDay('2024-11-16') // '11/16 (토)'
 */
export const formatDateShortWithDay = (date: Date | string): string => {
  if (!date) return '-';
  const d = new Date(date);
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const dayOfWeek = DAY_NAMES[d.getDay()];
  return `${month}/${day} (${dayOfWeek})`;
};
